import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthProvider';
import { useGetAiAssistantsQuery } from '../../generated/graphqlModels';
import withFlag from '../../helpers/withFlag';
import { Container, FloatingButton } from './components';
import { MessageWithAssistant } from './types';

interface AiChatProps {
  isTeamChatVisible: boolean;
}

const callbacks: any = {};
let id = 0;

export const useRefreshChat = () => {
  const [localId, setLocalId] = useState(id);
  const [key, setKey] = useState(0);

  const refresh = () => {
    Object.values(callbacks).forEach((callback: any) => {
      if (callback) {
        callback();
      }
    });
  };

  const localRefresh = () => {
    setKey((k) => k + 1);
  };

  useEffect(() => {
    setLocalId(id);
    id += 1;
    callbacks[localId] = localRefresh;
    return () => {
      callbacks[localId] = null;
    };
  }, []);

  return { key, refresh };
};

const AiChat = (props: AiChatProps) => {
  const { isTeamChatVisible } = props;
  const [isVisible, setIsVisible] = useState(false);
  const [initialAssistantId, setInitialAssistantId] = useState<
    string | undefined
  >();
  const [preferedThreadId, setPreferedThreadId] = useState<
    string | undefined
  >();
  const { key } = useRefreshChat();
  const user = useContext(AuthContext);

  const { data, loading, refetch } = useGetAiAssistantsQuery();

  useEffect(() => {
    // Do one fast refetch and then constant one minute refetch
    setTimeout(() => {
      refetch();
    }, 10000);
    const interval = setInterval(() => {
      refetch();
    }, 60000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    refetch();
  }, [key]);

  useEffect(() => {
    if (!isVisible) {
      // Hide floating button when chat is visible
      const floatingBtn = document.querySelector(
        '.chat-widget--button',
      ) as HTMLElement;
      if (floatingBtn) {
        floatingBtn.style.display = 'none';
      }
    }
  }, [isVisible]);

  const toggleChat = (assistantId?: string, threadId?: string) => {
    setInitialAssistantId(assistantId);
    setPreferedThreadId(threadId);
    if (isVisible) {
      refetch();
    }
    setIsVisible((v) => !v);
  };

  const chatAiAssistants = data?.aiAssistants
    ?.filter((a) => a.type === 'chat')
    .sort((a, b) => {
      if (initialAssistantId) {
        if (a.id === initialAssistantId) return -1;
        if (b.id === initialAssistantId) return 1;
      }
      return 0;
    });

  if (loading || !chatAiAssistants || chatAiAssistants.length === 0) {
    return null;
  }
  const aiAssistants = chatAiAssistants.filter((a) =>
    a.namespaces.some((n) => user?.namespaces.includes(n)),
  );

  if (!aiAssistants || aiAssistants.length === 0) {
    return null;
  }

  const newMessages = isVisible
    ? []
    : aiAssistants
        .reduce((acc, cur) => {
          const tmpMsgs =
            cur.newMessages?.map((msg) => ({
              ...msg,
              assistantId: cur.id,
              assistantName: cur.name,
            })) || [];
          return [...acc, ...tmpMsgs];
        }, [] as MessageWithAssistant[])
        .filter((msg) => {
          const timestamp = new Date(msg.createdAt);
          const oneMinuteAgo = new Date(new Date().getTime() - 60000); // 60000 ms = 1 minute
          const happenedInLastMinute = timestamp >= oneMinuteAgo;
          return happenedInLastMinute;
        });

  return (
    <div>
      {!isVisible && (
        <FloatingButton
          isTeamChatVisible={isTeamChatVisible}
          newMessages={newMessages}
          onClick={toggleChat}
        />
      )}
      {isVisible && data?.aiAssistants.length && (
        <Container
          isTeamChatVisible={isTeamChatVisible}
          assistants={aiAssistants}
          preferedThreadId={preferedThreadId}
          onClose={() => setIsVisible(false)}
        />
      )}
    </div>
  );
};

export default withFlag(AiChat, 'new-ai-feature');
